import axios from 'axios';
import type { AxiosProgressEvent } from 'axios';

import { getAPIUrls } from '../../../util/env';
import { getParsedAccessToken } from '../../callback/callBack.service';
import { buildAuthHeaders } from '../../../services/auth-headers';
import { track } from '../../../services/telemetry';
import { store } from '../../../store/store';
import { parseJwt } from '../../../util/jwt';
import { assetApi, type DiscardUploadsResponse } from '../services/assetApi';
import type { Asset } from '../../../types/asset.types';

export interface UploadContext {
  propertyUUID: string;
  orderUUID: string;
  photosetId?: string;
  // Virtual Staging tab upload: the created asset is persisted staged=true
  // so it lands in that tab (backend: AssetsController#uploadPhoto).
  staged?: boolean;
  // Floor Plan tab upload (DV-3735): the created asset carries this caption,
  // which is what makes it a floor plan. No sortOrder is sent, so asset-service
  // puts it at the end of the photoset.
  caption?: string;
}

export async function uploadAsset(
  file: File,
  ctx: UploadContext,
  onProgress: (pct: number) => void,
  signal?: AbortSignal,
): Promise<Asset> {
  const url = getAPIUrls('/asset-service/api/v1/assets/upload');
  const form = new FormData();
  form.append('file', file);
  form.append('propertyUUID', ctx.propertyUUID);
  form.append('orderUUID', ctx.orderUUID);
  if (ctx.photosetId) form.append('photosetId', ctx.photosetId);
  if (ctx.staged) form.append('staged', 'true');
  if (ctx.caption) form.append('caption', ctx.caption);

  const res = await axios.post<Asset>(url, form, {
    signal,
    onUploadProgress: (e: AxiosProgressEvent) => {
      // Without a computable total, the file's own size says when the body is all sent — the
      // caller relies on 100 to stop aborting an upload the server may already have saved.
      const total = e.total || file.size;
      if (total) onProgress(Math.min(100, Math.round((e.loaded / total) * 100)));
    },
  });
  return res.data;
}

/**
 * Why asset-service refused to discard a photo (DiscardUploadsResponse.Reason).
 * A rejection is final: retrying it changes nothing.
 */
export type DiscardRejectionReason = DiscardUploadsResponse['rejected'][number]['reason'];

export interface DiscardOutcome {
  /** Out of the photoset now (discarded by this call, earlier, or already gone). */
  discarded: string[];
  /** Refused by asset-service; left in place. */
  rejected: { assetUUID: string; reason: DiscardRejectionReason }[];
  /**
   * Not reached: the request kept failing, or the id was not a usable UUID.
   * The photo may still be in the photoset.
   */
  failed: string[];
}

// asset-service's MAX_DISCARD_BATCH.
const DISCARD_BATCH_SIZE = 200;
// Network errors and 5xx are retried this many times after the first try.
const DISCARD_RETRY_DELAYS_MS = [1000, 3000];

const DISCARD_PATH = '/asset-service/api/v1/assets/discard-uploads';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// RTK Query rejects with { status }: a number for an HTTP answer, a string
// ('FETCH_ERROR', 'TIMEOUT_ERROR', ...) when there was none — and
// { status: 'PARSING_ERROR', originalStatus } for an answer whose body was not
// JSON (an HTML 404 from a gateway, say).
const statusOf = (error: unknown): number | undefined => {
  const e = error as { status?: unknown; originalStatus?: unknown } | null;
  if (typeof e?.status === 'number') return e.status;
  return typeof e?.originalStatus === 'number' ? e.originalStatus : undefined;
};

// No response (network) or a server error: worth another try. A 4xx is not.
const isRetryable = (error: unknown) => {
  const status = statusOf(error);
  return status === undefined || status >= 500;
};

/**
 * Discards photos that were uploaded but never confirmed.
 *
 * Upload is eager — /assets/upload persists the asset, stores the original in S3,
 * kicks off resize/watermark, back-links it into the photoset (marking the set
 * unsynced) AND publishes it to Legacy. So abandoning the modal after a file
 * finishes leaves a real asset that shows up in the grid and can reach the PMS on
 * the next sync. Aborting the request is not enough once it has settled.
 *
 * The discard endpoint (assetApi.discardUploads) removes each photo from the
 * photoset and mirrors the delete to Legacy, in one request per batch. The old
 * query-param hard delete (DELETE /assets?assetUUID=) touched Connect only, so
 * Legacy's next photoset re-send put the photo back into the delivered set,
 * uncaptioned (orders 948042, 947961). A discarded upload is not listed in the
 * trash and cannot be recovered. A user-facing delete still goes through
 * assetApi.trashAsset (DV-2524), which is recoverable for 30 days.
 *
 * Requires asset-service #252, which must be deployed first: an older one
 * answers 405 and every photo comes back in `failed` (and is reported to the
 * user). There is deliberately no fallback to the old hard delete — it does not
 * tell Legacy, so it brings back the bug this endpoint fixes.
 *
 * Never throws: every id ends up in exactly one of the outcome's lists, and
 * anything other than a clean discard is logged.
 */
export async function discardUploads(assetUUIDs: string[]): Promise<DiscardOutcome> {
  const outcome: DiscardOutcome = { discarded: [], rejected: [], failed: [] };
  const valid: string[] = [];
  Array.from(new Set(assetUUIDs)).forEach((id) => {
    if (UUID_PATTERN.test(id)) {
      valid.push(id);
    } else {
      // An upload response without a usable assetUUID: nothing addressable to
      // discard, but the photo may exist, so the caller must hear about it.
      logDiscardProblem('invalid-asset-uuid', { assetUUID: String(id) });
      outcome.failed.push(id);
    }
  });
  for (let i = 0; i < valid.length; i += DISCARD_BATCH_SIZE) {
    await discardBatch(valid.slice(i, i + DISCARD_BATCH_SIZE), outcome);
  }
  if (outcome.rejected.length > 0) {
    logDiscardProblem('rejected', {
      rejected: outcome.rejected.map((r) => `${r.assetUUID}:${r.reason}`).join(','),
    });
  }
  if (outcome.failed.length > 0) {
    logDiscardProblem('failed', { assetUUIDs: outcome.failed.join(',') });
  }
  return outcome;
}

async function postDiscardBatch(batch: string[]): Promise<DiscardUploadsResponse> {
  // Same seam as edit/assetEditSeams: a plain function dispatching the RTK
  // Query mutation, so the hook's unmount net (which cannot use hooks) can call it.
  const sub = store.dispatch(assetApi.endpoints.discardUploads.initiate(batch));
  try {
    return await sub.unwrap();
  } finally {
    // No hook lifecycle resets a manually dispatched mutation's cache entry.
    sub.reset();
  }
}

async function discardBatch(batch: string[], outcome: DiscardOutcome): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await postDiscardBatch(batch);
      const discarded = res?.discarded ?? [];
      const rejected = res?.rejected ?? [];
      outcome.discarded.push(...discarded);
      outcome.rejected.push(...rejected);
      // Every id must land in exactly one list: one the server left out of both is unaccounted
      // for, so it counts as failed.
      const answered = new Set([...discarded, ...rejected.map((r) => r.assetUUID)]);
      outcome.failed.push(...batch.filter((id) => !answered.has(id)));
      return;
    } catch (error) {
      if (!isRetryable(error) || attempt >= DISCARD_RETRY_DELAYS_MS.length) {
        logDiscardProblem('request-failed', {
          status: String(statusOf(error) ?? 'network'),
          count: String(batch.length),
        });
        outcome.failed.push(...batch);
        return;
      }
      await sleep(DISCARD_RETRY_DELAYS_MS[attempt]);
    }
  }
}

/**
 * Last-chance discard while the page is being torn down (tab close, reload,
 * navigation away). An RTK/axios request is cancelled with the page; a keepalive
 * fetch is allowed to outlive it. Synchronous on purpose — nothing can be awaited
 * in a pagehide handler, so it uses the stored token as is rather than refreshing
 * it, and sends nothing when that token has already expired (a 401 nobody sees).
 * Best-effort: the response is never seen, and a browser may drop the CORS
 * preflight this request needs during unload.
 *
 * Returns whether a request was sent, so the caller can keep unsent photos retryable.
 */
export function discardUploadsOnUnload(assetUUIDs: string[]): boolean {
  const valid = assetUUIDs.filter((id) => UUID_PATTERN.test(id));
  if (valid.length === 0) return false;
  const token = getParsedAccessToken()?.access_token ?? null;
  if (!token || isExpired(token)) {
    logDiscardProblem('unload-token-expired', { count: String(valid.length) });
    return false;
  }
  const headers = { 'Content-Type': 'application/json', ...buildAuthHeaders(token) };
  for (let i = 0; i < valid.length; i += DISCARD_BATCH_SIZE) {
    void fetch(getAPIUrls(DISCARD_PATH), {
      method: 'POST',
      headers,
      body: JSON.stringify({ assetUUIDs: valid.slice(i, i + DISCARD_BATCH_SIZE) }),
      keepalive: true,
    }).catch(() => undefined);
  }
  return true;
}

const isExpired = (token: string) => {
  try {
    return parseJwt(token).exp <= Math.floor(Date.now() / 1000);
  } catch {
    return true;
  }
};

function logDiscardProblem(kind: string, details: Record<string, string>): void {
  track('upload-discard-problem', { kind, ...details });
}
